import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { WarningIcon as Warning, ArrowSquareOutIcon as ArrowSquareOut, ReceiptIcon as Receipt } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Red banner rendered on Dashboard + Billing when the calling user has
 * a subscription in past_due state. Single CTA opens the Stripe Billing
 * Portal so the user can update their card; secondary link drops to
 * the hosted invoice if one is available.
 */
export default function PaymentRecoveryBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const native = isNativeApp();

  useEffect(() => {
    if (native) return;
    api.get("/billing/payment-status", { silent: true })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ past_due: false }));
  }, []);

  if (native || !status || !status.past_due) return null;

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/customer-portal", {
        return_url: window.location.href,
      });
      if (data?.url) window.location.href = data.url;
      else throw new Error("Couldn't open the billing portal. Please try again.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return null; }
  };
  const nextRetry = fmtDate(status.next_retry_at);

  return (
    <div
      className="mb-6 rounded-xl border-2 border-crimson/40 bg-[#FFF0F0] p-4 sm:p-5"
      data-testid="payment-recovery-banner"
    >
      <div className="flex items-start gap-3">
        <span className="p-2 rounded-full bg-[#FDEEEE] shrink-0">
          <Warning size={20} weight="fill" className="text-crimson" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-spectral text-[17px] sm:text-lg leading-tight">
            Payment failed on your {status.plan_label || "subscription"}
          </h3>
          <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">
            We couldn't charge your card{status.failure_count > 1 ? ` (attempt #${status.failure_count})` : ""}.
            {nextRetry ? ` Stripe will retry on ${nextRetry}.` : ""} Update your
            payment method now to avoid losing your benefits.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <Button
              onClick={openPortal}
              disabled={busy}
              data-testid="payment-recovery-portal-btn"
              className="bg-ink hover:bg-crimson text-white rounded-full h-10 px-5"
            >
              Update payment method <ArrowSquareOut size={13} weight="bold" className="ml-1" />
            </Button>
            {status.hosted_invoice_url && (
              <a
                href={status.hosted_invoice_url}
                target="_blank"
                rel="noreferrer"
                data-testid="payment-recovery-invoice-link"
                className="text-[12px] font-bold text-ink hover:text-crimson underline inline-flex items-center gap-1"
              >
                <Receipt size={13} weight="bold" /> View invoice
              </a>
            )}
            <Link
              to="/billing"
              className="text-[12px] text-[#6B6B6B] hover:text-ink underline"
              data-testid="payment-recovery-billing-link"
            >
              Manage billing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
